import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  Chip,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Paper,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { AuditTrailEntryDTO, AuditAction } from '../dtos';
import { QuestionWithResponsesForAudit } from '../types';
import { AuditTrailEntry } from './AuditTrailEntry';

interface AuditTrailDialogProps {
  open: boolean;
  onClose: () => void;
  auditId: string;
  surveyId: string;
  auditTrail: AuditTrailEntryDTO[];
  questions: QuestionWithResponsesForAudit[];
  isLoading?: boolean;
}

export const AuditTrailDialog: React.FC<AuditTrailDialogProps> = ({
  open,
  onClose,
  auditId: _auditId,
  surveyId: _surveyId,
  auditTrail,
  questions,
  isLoading = false,
}) => {
  const [filterAction, setFilterAction] = useState<AuditAction | 'ALL'>('ALL');
  const [searchText, setSearchText] = useState('');

  const filteredTrail = auditTrail.filter((entry) => {
    const matchesAction = filterAction === 'ALL' || entry.action === filterAction;

    if (!searchText) {
      return matchesAction;
    }

    // Enhanced search that includes display names and question text
    const searchLower = searchText.toLowerCase();

    // Find the question to get its display text
    const question = questions.find((q) => q.questionId === entry.questionId);
    const questionText = question?.questionText || '';

    // Find the response to get its content
    const response = question?.responses.find((r) => r.id === entry.responseId);
    const responseContent = response ? String(response.responseValue) : '';

    const matchesSearch =
      entry.questionId.toLowerCase().includes(searchLower) ||
      entry.responseId.toLowerCase().includes(searchLower) ||
      entry.userId.toLowerCase().includes(searchLower) ||
      questionText.toLowerCase().includes(searchLower) ||
      responseContent.toLowerCase().includes(searchLower);

    return matchesAction && matchesSearch;
  });

  const sortedTrail = filteredTrail.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  const getActionStats = () => {
    const stats = auditTrail.reduce(
      (acc, entry) => {
        acc[entry.action] = (acc[entry.action] || 0) + 1;
        return acc;
      },
      {} as Record<AuditAction, number>
    );

    return stats;
  };

  const actionStats = getActionStats();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Audit Trail</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Complete history of all audit actions performed • Search by question text, response
          content, or user information
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Summary Stats */}
        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Action Summary
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={`${actionStats.RESPONSE_INCLUDED || 0} responses included`}
              color="success"
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${actionStats.RESPONSE_EXCLUDED || 0} responses excluded`}
              color="error"
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${actionStats.COMMENT_INCLUDED || 0} comments included`}
              color="info"
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${actionStats.COMMENT_EXCLUDED || 0} comments excluded`}
              color="warning"
              size="small"
              variant="outlined"
            />
          </Box>
        </Paper>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            size="small"
            placeholder="Search by question text, response content, or user name..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filter Action</InputLabel>
            <Select
              value={filterAction}
              label="Filter Action"
              onChange={(e) => setFilterAction(e.target.value as AuditAction | 'ALL')}
            >
              <MenuItem value="ALL">All Actions</MenuItem>
              <MenuItem value="RESPONSE_INCLUDED">Response Included</MenuItem>
              <MenuItem value="RESPONSE_EXCLUDED">Response Excluded</MenuItem>
              <MenuItem value="COMMENT_INCLUDED">Comment Included</MenuItem>
              <MenuItem value="COMMENT_EXCLUDED">Comment Excluded</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Trail List */}
        {isLoading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography>Loading audit trail...</Typography>
          </Box>
        ) : sortedTrail.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {auditTrail.length === 0
                ? 'No audit actions recorded yet'
                : 'No actions match the current filters'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {sortedTrail.map((entry, index) => {
              const isLastItem = index === sortedTrail.length - 1;

              return (
                <React.Fragment key={`${entry.timestamp}-${entry.responseId}`}>
                  <AuditTrailEntry entry={entry} questions={questions} />
                  {!isLastItem && <Divider variant="inset" component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
